import { z } from "zod";
import {
	AUDIO_TYPE,
	AUDIO_GROUP_TYPE,
	COLLECTION_NAMES,
	COLLECTION_SLUGS,
	EFFECT_NAMES,
	EFFECT_SLUGS,
} from "./dictionary";

export const collectionNameValues = Object.values(COLLECTION_NAMES);
export const collectionSlugValues = Object.values(COLLECTION_SLUGS);
export const effectNameValues = Object.values(EFFECT_NAMES);
export const effectSlugValues = Object.values(EFFECT_SLUGS);

export const audioSchema = z.object({
	id: z.string(), // "037fc865-2ebb-4ccc-80cc-ebd983235894",
	name: z.string().min(1, "Name is required"), // "Taipei Tones",
	slug: z.string().regex(/^[a-z0-9-]+$/), // "taipei-tones",
	description: z.string().optional(),
	isPremium: z.boolean(), // true,
	isLooping: z.boolean().default(true), // true,
	location: z
		.object({
			name: z.string().nullable(),
			address1: z.string().nullable(),
			address2: z.string().nullable(),
			cityOrLocality: z.string().nullable(),
			stateOrProvince: z.string().nullable(),
			country: z.string().nullable(),
		})
		.nullable(),
	albumId: z.string(), // "162358d8-a444-4dda-9528-72768721e37b",
	imageUrlStub: z.string(),
	collectionId: z.xor(
		[
			z
				.string()
				.regex(/^collections:[a-z0-9-]+$/), // "collection:collection-slug-value"
			z.literal("custom-blends"), // "custom-blend"
		],
		"Either collectionId or 'custom-blends' must be provided",
	),
	effectIds: z.array(
		z.string().regex(/^effects:[a-z0-9-]+$/),
		"Invalid Effect ID",
	), // Many-to-many via array
	releaseDate: z.iso.datetime({ local: true }), // "2025-06-25 20:40:50.977605+00",
	createdAt: z.iso.datetime({ local: true }), // "2025-06-25 20:40:50.977605+00",
	updatedAt: z.iso.datetime({ local: true }), // "2025-06-25 20:40:50.977605+00"
});

export const trackSchema = audioSchema.extend({
	type: z.literal(AUDIO_TYPE.TRACK), // "TRACK",
	audioUrlStub: z.string(),
	userId: z.literal(null), // null, users can only create blends
	blend: z.literal(null), // null,
});

export const blendedTrackSchema = audioSchema.extend({
	type: z.literal(AUDIO_TYPE.BLEND), // "BLEND",
	userId: z.string().nullable(), // could belong to a user or be null for system blends,
	blend: z.array(
		z.object({
			trackId: z.string(), // "037fc865-2ebb-4ccc-80cc-ebd983235894",
			volume: z
				.number()
				.min(0, "Volume must be at least 0")
				.max(1, "Volume must be at most 1"), // 0.8,
		}),
	),
});

export const baseGroupSchema = z.object({
	id: z.string(), // "collections:collection-slug-value" or "effects:effect-slug-value"
	name: z.string(),
	slug: z.string().regex(/^[a-z0-9-]+$/),
	description: z.string(),
	imageUrlStub: z.string(),
});

export const freeSchema = baseGroupSchema.extend({
	type: z.literal(AUDIO_GROUP_TYPE.FREE), // "FREE"
});

export const favoritesSchema = baseGroupSchema.extend({
	type: z.literal(AUDIO_GROUP_TYPE.FAVORITES), // "FAVORITES"
	tracks: z.array(z.string()), // array of track ids
});

export const customBlendGroupSchema = baseGroupSchema.extend({
	type: z.literal(AUDIO_GROUP_TYPE.CUSTOM_BLEND), // "CUSTOM_BLEND"
});

export const albumSchema = baseGroupSchema.extend({
	type: z.literal(AUDIO_GROUP_TYPE.ALBUM), // "ALBUM"
	collectionIds: z.array(z.string().regex(/^collections:[a-z0-9-]+$/)),
	releaseDate: z.iso.datetime({ local: true }), // for iso dates
	tracks: z.array(z.string()), // array of track ids
});

export const effectSchema = baseGroupSchema.extend({
	type: z.literal(AUDIO_GROUP_TYPE.EFFECT), // "EFFECT"
	id: z.string().regex(/^effects:[a-z0-9-]+$/), // "effects:effect-slug-value"
	name: z.enum(effectNameValues),
	slug: z.enum(effectSlugValues),
});

export const collectionSchema = baseGroupSchema.extend({
	type: z.literal(AUDIO_GROUP_TYPE.COLLECTION), // "COLLECTION"
	id: z.string().regex(/^collections:[a-z0-9-]+$/), // "collections:collection-slug-value"
	name: z.enum(collectionNameValues),
	slug: z.enum(collectionSlugValues),
});
